'use client';

import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'next/navigation';
import Link from 'next/link';
import { ProductMedia } from '@/components/product-media';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Package } from 'lucide-react';
import { PhoneVerification } from '@/components/phone-verification';
import { Spinner } from '@/components/ui/spinner';
import { toast } from 'sonner';
import { useTranslations } from 'next-intl';
import { getPaymentMethodLabel as translatePaymentMethod } from '@/lib/paymentLabels';
import { cn, formatCurrency } from '@/lib/utils';
import { displayTotalWithInstallmentCharge } from '@/lib/orderInstallmentDisplay';
import {
  getDisplayVariantDetails,
  resolveVariantAttributeLabel,
} from '@/lib/variantDetails';
import type { OrderStatus, OrderFulfillmentType } from '@/types/order';
import { FoodSelectionsDisplay } from '@/components/food-selections-display';
import type { ProductOptionGroup, ProductAdditional } from '@/lib/productService';

const STATUS_TRANSLATION_KEYS: Record<OrderStatus, string> = {
  PENDING: 'orders.status.pending',
  CONFIRMED: 'orders.status.confirmed',
  PREPARING: 'orders.status.preparing',
  READY: 'orders.status.ready',
  DELIVERING: 'orders.status.delivering',
  DELIVERED: 'orders.status.delivered',
  CANCELLED: 'orders.status.cancelled',
};

const STATUS_BADGE_STYLES: Record<
  OrderStatus,
  {
    badgeClass: string;
    dotClass: string;
  }
> = {
  PENDING: {
    badgeClass: 'bg-slate-50 text-slate-900 border-slate-200 dark:bg-slate-500/15 dark:text-slate-50 dark:border-slate-400/40',
    dotClass: 'bg-slate-500',
  },
  CONFIRMED: {
    badgeClass: 'bg-amber-50 text-amber-900 border-amber-200 dark:bg-amber-500/15 dark:text-amber-50 dark:border-amber-400/40',
    dotClass: 'bg-amber-500',
  },
  PREPARING: {
    badgeClass: 'bg-blue-50 text-blue-900 border-blue-200 dark:bg-blue-500/15 dark:text-blue-50 dark:border-blue-400/40',
    dotClass: 'bg-blue-500',
  },
  READY: {
    badgeClass: 'bg-sky-50 text-sky-900 border-sky-200 dark:bg-sky-500/15 dark:text-sky-50 dark:border-sky-400/40',
    dotClass: 'bg-sky-500',
  },
  DELIVERING: {
    badgeClass: 'bg-indigo-50 text-indigo-900 border-indigo-200 dark:bg-indigo-500/15 dark:text-indigo-50 dark:border-indigo-400/40',
    dotClass: 'bg-indigo-500',
  },
  DELIVERED: {
    badgeClass: 'bg-emerald-50 text-emerald-900 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-50 dark:border-emerald-400/40',
    dotClass: 'bg-emerald-500',
  },
  CANCELLED: {
    badgeClass: 'bg-rose-50 text-rose-900 border-rose-200 dark:bg-rose-500/15 dark:text-rose-50 dark:border-rose-400/40',
    dotClass: 'bg-rose-500',
  },
};

const DEFAULT_STATUS_STYLE = {
  badgeClass: 'bg-muted text-muted-foreground border-border',
  dotClass: 'bg-muted-foreground',
};


type OrderItem = {
  id: string;
  productId: string;
  productName?: string;
  productImageUrl?: string | null;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  notes?: string | null;
  variantDetails?: {
    [key: string]: string;
  };
  selections?: {
    optionGroups: Array<{ groupId: string; optionIds: string[] }>;
    additionals: Array<{ additionalId: string; quantity: number }>;
  };
};

/** Persisted freight; normalized at fetch so UI always has a number (0 = none / pickup). */
function normalizeDeliveryFee(o: {
  total: number;
  deliveryFee?: number;
  items: OrderItem[];
}): number {
  if (typeof o.deliveryFee === 'number' && Number.isFinite(o.deliveryFee)) {
    return o.deliveryFee;
  }
  const baseTotal = Number(o.total) || 0;
  const storedItemsTotal = o.items.reduce(
    (sum, item) => sum + (Number(item.totalPrice) || 0),
    0
  );
  return Math.max(0, baseTotal - storedItemsTotal);
}

type Order = {
  id: string;
  storeId: string;
  storeName: string;
  storeImageUrl: string | null;
  userPhone: string;
  userName: string;
  status: OrderStatus;
  fulfillmentType?: OrderFulfillmentType;
  total: number;
  deliveryFee: number;
  paymentMethod: string;
  cashExchangeAmount?: number | null;
  observation?: string | null;
  addressData: string | null;
  createdAt: string;
  items: OrderItem[];
  couponCode?: string | null;
  subtotalDiscountAmount?: number | null;
  deliveryDiscountAmount?: number | null;
  totalBeforeDiscount?: number | null;
  installments?: number | null;
  passedFeeCents?: number | null;
  chargedAmountCents?: number | null;
};

export default function OrdersPage() {
  const t = useTranslations();
  const params = useParams();
  const storeId = params.storeId as string;
  const locale = params.locale as string;

  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [orders, setOrders] = useState<Order[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [productDataMap, setProductDataMap] = useState<Map<string, { optionGroups: ProductOptionGroup[]; additionals: ProductAdditional[]; basePrice: number }>>(new Map());
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const loadMoreFnRef = useRef<() => void>(() => { });

  const handlePhoneVerified = async (phoneValue: string) => {
    setPhone(phoneValue);
    setIsAuthenticated(true);
    await handleSearch(phoneValue);
  };

  const handlePhoneReset = async () => {
    // Clear server-side session cookie
    try {
      await fetch('/api/otp/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Error clearing session:', error);
      // Continue with local cleanup even if API call fails
    }

    // Clear local state
    setPhone('');
    setIsAuthenticated(false);
    setOrders([]);
    setHasSearched(false);
    setError(null);
    setHasMore(false);
  };

  const fetchProductDataForOrders = async (
    newOrders: Order[],
    currentMap: Map<string, { optionGroups: ProductOptionGroup[]; additionals: ProductAdditional[]; basePrice: number }>
  ): Promise<Map<string, { optionGroups: ProductOptionGroup[]; additionals: ProductAdditional[]; basePrice: number }>> => {
    const itemsWithSelections = newOrders.flatMap((order) =>
      order.items.filter((item) => item.selections)
    );
    if (itemsWithSelections.length === 0) return currentMap;

    const productIds = (
      [...new Set(itemsWithSelections.map((item: OrderItem) => String(item.productId)))] as string[]
    ).filter((id) => !currentMap.has(id));

    if (productIds.length === 0) return currentMap;

    const updatedMap = new Map(currentMap);
    await Promise.all(
      productIds.map(async (productId) => {
        try {
          const res = await fetch(`/api/products/${productId}`);
          if (res.ok) {
            const product = await res.json();
            updatedMap.set(productId, {
              optionGroups: product.optionGroups || [],
              additionals: product.additionals || [],
              basePrice:
                product.promotionPrice && product.promotionPrice > 0
                  ? product.promotionPrice
                  : product.price,
            });
          }
        } catch (err) {
          console.error(`Failed to fetch product data for ${productId}:`, err);
        }
      })
    );
    return updatedMap;
  };

  const handleSearch = async (phoneOverride?: string) => {
    const targetPhone = phoneOverride ?? phone;
    if (!targetPhone) {
      return;
    }

    setIsLoading(true);
    setError(null);
    setHasSearched(true);
    setOrders([]);
    setHasMore(false);

    try {
      const response = await fetch(
        `/api/orders?phone=${encodeURIComponent(targetPhone)}&storeId=${storeId}&locale=${locale}&skip=0&take=10`,
        { cache: 'no-store' }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }

      const data = await response.json();
      const rawOrders = data.orders || [];
      const fetchedOrders: Order[] = rawOrders.map((o: Order) => ({
        ...o,
        deliveryFee: normalizeDeliveryFee(o),
      }));
      setOrders(fetchedOrders);
      setHasMore(data.hasMore ?? false);

      const updatedMap = await fetchProductDataForOrders(fetchedOrders, new Map());
      setProductDataMap(updatedMap);

      if (fetchedOrders.length === 0) {
        toast.info(t('orders.noOrdersFound'));
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error(t('orders.fetchError') || t('errors.generic'));
      setError(t('orders.fetchError') || t('errors.generic'));
      setOrders([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMore = async () => {
    if (!phone || isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    try {
      const response = await fetch(
        `/api/orders?phone=${encodeURIComponent(phone)}&storeId=${storeId}&locale=${locale}&skip=${orders.length}&take=10`,
        { cache: 'no-store' }
      );

      if (!response.ok) throw new Error('Failed to fetch orders');

      const data = await response.json();
      const rawOrders = data.orders || [];
      const newOrders: Order[] = rawOrders.map((o: Order) => ({
        ...o,
        deliveryFee: normalizeDeliveryFee(o),
      }));

      setOrders((prev) => [...prev, ...newOrders]);
      setHasMore(data.hasMore ?? false);

      if (newOrders.length > 0) {
        const updatedMap = await fetchProductDataForOrders(newOrders, productDataMap);
        setProductDataMap(updatedMap);
      }
    } catch (err) {
      console.error('Error loading more orders:', err);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Keep ref current so the IntersectionObserver always calls the latest version
  loadMoreFnRef.current = loadMore;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreFnRef.current();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
    // Re-attach when orders list appears or hasMore changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders.length > 0, hasMore]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    if (locale === 'pt-BR') {
      return date.toLocaleDateString('pt-BR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getPaymentLabel = (method: string | null) => translatePaymentMethod(method, t);
  const getStatusLabel = (status: OrderStatus) => {
    const key = STATUS_TRANSLATION_KEYS[status] ?? STATUS_TRANSLATION_KEYS.CONFIRMED;
    return t(key);
  };

  const getStatusStyle = (status: OrderStatus) => {
    return STATUS_BADGE_STYLES[status] ?? DEFAULT_STATUS_STYLE;
  };

  // Calculate item price including selections
  const calculateItemPriceWithSelections = (
    basePrice: number,
    quantity: number,
    selections: OrderItem['selections'] | undefined,
    optionGroups: ProductOptionGroup[],
    additionals: ProductAdditional[]
  ): number => {
    if (!selections) {
      return Number(basePrice) * Number(quantity);
    }

    let pricePerUnit = Number(basePrice) || 0;

    // Add option price modifiers
    if (selections.optionGroups) {
      selections.optionGroups.forEach((groupSelection) => {
        const group = optionGroups.find(g => g.id === groupSelection.groupId);
        if (group) {
          groupSelection.optionIds.forEach(optionId => {
            const option = group.options.find(o => o.id === optionId);
            if (option) {
              pricePerUnit += Number(option.priceModifier) || 0;
            }
          });
        }
      });
    }

    // Add additional prices
    if (selections.additionals) {
      selections.additionals.forEach((additionalSelection) => {
        const additional = additionals.find(a => a.id === additionalSelection.additionalId);
        if (additional && additionalSelection.quantity > 0) {
          const additionalPrice = Number(additional.price) || 0;
          const additionalQty = Number(additionalSelection.quantity) || 0;
          pricePerUnit += additionalPrice * additionalQty;
        }
      });
    }

    return pricePerUnit * Number(quantity);
  };

  // Pre-calculate totals for all orders
  const orderTotals = useMemo(() => {
    const totals = new Map<string, number>();
    orders.forEach((order) => {
      const itemsTotal = order.items.reduce((sum, item) => {
        const productData = productDataMap.get(item.productId);
        const itemTotalPrice = item.selections && productData
          ? calculateItemPriceWithSelections(
            productData.basePrice,
            item.quantity,
            item.selections,
            productData.optionGroups,
            productData.additionals
          )
          : Number(item.totalPrice) || 0;
        return Number(sum) + Number(itemTotalPrice);
      }, 0);

      const couponDiscount = (order.subtotalDiscountAmount ?? 0) + (order.deliveryDiscountAmount ?? 0);
      totals.set(order.id, Math.max(0, Number(itemsTotal) + order.deliveryFee - couponDiscount));
    });
    return totals;
  }, [orders, productDataMap]);

  return (
    <div>
      <div className="mb-4">
        <h1 className="text-base font-medium leading-snug mb-2">
          {t('orders.title')}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t('orders.description')}
        </p>
      </div>

      {/* Phone Verification */}
      <div className="mb-4">
        <PhoneVerification
          onVerified={handlePhoneVerified}
          onReset={handlePhoneReset}
          showChangePhoneButton={hasSearched && isAuthenticated}
          phoneLabel={t('orders.phoneNumber')}
          phoneDescription={!hasSearched ? t('orders.description') : undefined}
        />
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex flex-col items-center justify-center py-12">
          <Spinner />
          <p className="text-muted-foreground mt-4">{t('orders.loading')}</p>
        </div>
      )}

      {/* No Orders Found */}
      {!isLoading && hasSearched && orders.length === 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">
                {t('orders.noOrdersFound')}
              </h2>
              <p className="text-muted-foreground mb-6">
                {t('orders.noOrdersDescription')}
              </p>
              <Link href={`/${locale}/${storeId}/catalog`}>
                <Button>
                  {t('catalog.browseProducts')}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Orders List */}
      {!isLoading && orders.length > 0 && (
        <>
          <div className="space-y-6">
            {orders.map((order) => {
              const currentStatus: OrderStatus = order.status ?? 'CONFIRMED';
              const statusStyle = getStatusStyle(currentStatus);
              const recalculatedTotal = orderTotals.get(order.id) ?? (Number(order.total) || 0);
              const displayTotal = displayTotalWithInstallmentCharge(recalculatedTotal, {
                chargedAmountCents: order.chargedAmountCents ?? null,
                passedFeeCents: order.passedFeeCents ?? null,
                installments: order.installments ?? null,
              });
              const installmentAmount =
                order.installments && order.installments > 0 && order.chargedAmountCents != null
                  ? order.chargedAmountCents / order.installments / 100
                  : displayTotal / (order.installments || 1);
              const passedFeeAmount = (order.passedFeeCents ?? 0) / 100;
              const fulfillmentLabel = order.fulfillmentType === 'PICKUP'
                ? t('orders.fulfillment.pickup')
                : t('orders.fulfillment.delivery');
              const showCashExchange =
                order.fulfillmentType !== 'PICKUP' &&
                order.paymentMethod === 'CASH' &&
                typeof order.cashExchangeAmount === 'number';
              const summaryGridClass = showCashExchange
                ? 'grid grid-cols-1 md:grid-cols-3 gap-4'
                : 'grid grid-cols-1 md:grid-cols-2 gap-4';

              return (
                <Card key={order.id}>
                  <CardHeader>
                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Package className="w-5 h-5" />
                          {t('orders.orderNumber')} {order.id.split('-')[0]}
                        </CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                          {formatDate(order.createdAt)}
                        </p>
                      </div>
                      <div className="flex flex-col gap-1 md:items-end">
                        <span className="text-xs font-medium uppercase text-muted-foreground tracking-wide">
                          {t('orders.statusLabel')}
                        </span>
                        <Badge
                          variant="secondary"
                          className={cn(
                            'flex items-center gap-2 rounded-full px-3 py-1 text-[0.65rem] font-semibold tracking-wide border',
                            statusStyle.badgeClass
                          )}
                        >
                          <span className={cn('h-2 w-2 rounded-full', statusStyle.dotClass)} />
                          {getStatusLabel(currentStatus)}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="text-muted-foreground">
                        {t('orders.fulfillment.label')}:
                      </span>
                      <Badge variant="outline">{fulfillmentLabel}</Badge>
                    </div>

                    {/* Order Items */}
                    <div className="space-y-3">
                      {order.items.map((item) => (
                        <div key={item.id} className="pb-3 border-b last:border-b-0">
                          <div className="flex gap-3 mb-2">
                            {item.productImageUrl && (
                              <div className="relative w-16 h-16 flex-shrink-0 bg-muted rounded overflow-hidden">
                                <ProductMedia
                                  src={item.productImageUrl}
                                  alt={item.productName || item.productId}
                                  fill
                                  sizes="64px"
                                  className="object-cover"
                                  autoPlay
                                  loop
                                />
                              </div>
                            )}
                            <div className="flex-grow">
                              <div className="flex items-start gap-2 mb-1">
                                <p className="text-sm font-medium flex-1">
                                  {item.productName || item.productId}
                                </p>
                                {item.notes && (
                                  <span className="text-xs text-muted-foreground italic flex-shrink-0">
                                    ({item.notes})
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground mb-1">
                                {item.quantity} × {formatCurrency(item.unitPrice, locale)}
                              </p>
                              <p className="text-sm font-semibold text-foreground">
                                {formatCurrency(item.totalPrice, locale)}
                              </p>
                            </div>
                          </div>
                          {/* Variant Details and Food Selections */}
                          {(item.variantDetails || item.selections) && (
                            <div>
                              {/* Variant Details */}
                              {(() => {
                                const displayVariantDetails = getDisplayVariantDetails(
                                  item.variantDetails
                                );
                                if (displayVariantDetails.length === 0) {
                                  return null;
                                }

                                return (
                                  <div className="flex flex-wrap gap-2 mb-2">
                                    {displayVariantDetails.map(([key, value]) => (
                                      <span
                                        key={key}
                                        className="text-xs px-2 py-1 bg-muted rounded-md text-muted-foreground"
                                      >
                                        {resolveVariantAttributeLabel(key, t, t.has)}: {value}
                                      </span>
                                    ))}
                                  </div>
                                );
                              })()}
                              {/* Food Selections */}
                              {item.selections && (
                                <FoodSelectionsDisplay
                                  productId={item.productId}
                                  selections={item.selections}
                                  locale={locale}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    <Separator />

                    {order.observation && (
                      <>
                        <div className="pt-4">
                          <p className="text-sm text-muted-foreground mb-1">
                            {t('orders.observationLabel')}
                          </p>
                          <p className="text-sm font-medium whitespace-pre-wrap">
                            {order.observation}
                          </p>
                        </div>
                        <Separator />
                      </>
                    )}

                    {/* Order Summary */}
                    <div className="pt-4">
                      <div className={summaryGridClass}>
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">
                            {t('confirmation.paymentMethod')}
                          </p>
                          <p className="text-base font-medium">
                            {getPaymentLabel(order.paymentMethod)}
                          </p>
                          {order.installments != null && order.installments > 1 && (
                            <p className="text-sm text-muted-foreground">
                              {t('checkout.installments.summary', {
                                count: order.installments,
                                amount: formatCurrency(installmentAmount, locale),
                              })}
                            </p>
                          )}
                          {passedFeeAmount > 0 && (
                            <p className="text-sm text-muted-foreground">
                              {t('checkout.installments.feeLine')}: {formatCurrency(passedFeeAmount, locale)}
                            </p>
                          )}
                        </div>
                        {showCashExchange && (
                          <div>
                            <p className="text-sm text-muted-foreground mb-1">
                              {t('orders.cashExchange.label')}
                            </p>
                            <p className="text-base font-medium text-foreground">
                              {formatCurrency(order.cashExchangeAmount || 0, locale)}
                            </p>
                          </div>
                        )}
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">
                            {t('checkout.total')}
                          </p>
                          {order.couponCode && order.totalBeforeDiscount ? (
                            <div className="space-y-0.5">
                              <p className="text-sm text-muted-foreground line-through">
                                {formatCurrency(order.totalBeforeDiscount, locale)}
                              </p>
                              <p className="text-2xl font-bold text-primary">
                                {formatCurrency(displayTotal, locale)}
                              </p>
                              <p className="text-xs text-green-700 dark:text-green-400 font-medium">
                                Cupom {order.couponCode}
                                {' '}
                                ({formatCurrency(
                                  (order.subtotalDiscountAmount ?? 0) + (order.deliveryDiscountAmount ?? 0),
                                  locale
                                )} de desconto)
                              </p>
                            </div>
                          ) : (
                            <p className="text-2xl font-bold text-foreground">
                              {formatCurrency(displayTotal, locale)}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="mt-4">
                        <Link href={`/${locale}/${storeId}/order/${order.id}`}>
                          <Button variant="outline" className="w-full md:w-auto">
                            {t('orders.viewDetails')}
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Infinite scroll sentinel */}
          <div ref={sentinelRef} className="h-1" />

          {isLoadingMore && (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          )}

          {!hasMore && (
            <p className="text-center text-sm text-muted-foreground py-4">
              {t('orders.allOrdersLoaded')}
            </p>
          )}
        </>
      )}
    </div>
  );
}
